import React from 'react';
import { X, MapPin, Search } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';

const FilterModal = ({ 
  isOpen, 
  onClose, 
  searchCity, 
  onSearchCityChange, 
  onSearchCityFocus,
  onSearchCityBlur,
  onKeyPress,
  suggestions,
  showSuggestions,
  onSuggestionSelect,
  selectedFilters,
  onToggleFilter,
  selectedTimeFilter,
  onTimeFilterChange,
  runFilters,
  timeFilters,
  onClearFilters
}) => {
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-transparent border-none shadow-none max-w-fit p-0 [&>button]:hidden">
        {/* Hidden accessibility elements */}
        <DialogHeader className="sr-only">
          <DialogTitle>Filter Runs</DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          {/* Custom Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors duration-200 focus:outline-none"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Filter Modal Content */}
          <div className="w-[90vw] max-w-md bg-black/95 backdrop-blur-sm rounded-3xl shadow-2xl border-2 border-white/20 p-6">
            <div className="space-y-6">
              {/* Header */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Filter Runs
                </h2>
                <p className="text-gray-300 text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Find the perfect run for you
                </p>
              </div>

              {/* City Search */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-300" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Search by City
                </label>
                <div className="relative">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Search by city..."
                      value={searchCity}
                      onChange={onSearchCityChange}
                      onKeyPress={onKeyPress}
                      onFocus={onSearchCityFocus}
                      onBlur={onSearchCityBlur}
                      className="pl-10 pr-4 py-3 text-sm bg-white/10 border-white/20 text-white placeholder-gray-400 focus:bg-white/20 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/20 rounded-lg"
                      style={{fontFamily: 'Montserrat, sans-serif'}}
                    />
                  </div>
                  
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 bg-black/90 border border-white/20 rounded-lg mt-1 z-50 shadow-lg max-h-48 overflow-y-auto">
                      {suggestions.map((city, index) => (
                        <button
                          key={index}
                          onClick={() => onSuggestionSelect(city)}
                          className="w-full text-left px-4 py-3 text-white hover:bg-white/10 hover:text-white transition-colors text-sm"
                          style={{fontFamily: 'Montserrat, sans-serif'}}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* What Kind of Run Filters */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-300" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  What Kind of Run
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {runFilters.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => onToggleFilter(filter)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedFilters.includes(filter)
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      style={{fontFamily: 'Montserrat, sans-serif'}}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Filters */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-300" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Time
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {timeFilters.map((filter) => (
                    <button
                      key={filter}
                      onClick={() => onTimeFilterChange(filter)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedTimeFilter === filter
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      style={{fontFamily: 'Montserrat, sans-serif'}}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-white/20">
                <Button
                  onClick={onClearFilters}
                  variant="outline"
                  className="flex-1 text-white border-white/20 hover:bg-white/10"
                >
                  Clear All
                </Button>
                <Button
                  onClick={onClose}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilterModal;
